import { Box, Button, InputAdornment, Link, TextField, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { CaseType } from '../../types/SimilarlySituated/CaseType.type.ts';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { SsbEntryPoint } from '../../types/enums/SsbEntryPoint.enum';
import { MAX_CONDITIONS } from '../../utils/ssbBuilderBox';
import { deriveCaseTypeName } from '../../utils/ssbCondition';
import ConditionChip from './ConditionChip';
import ConditionPanel from './ConditionPanel';

interface CaseTypeCardProps {
  caseType: CaseType;
  countyId?: number | null;
  canDuplicate: boolean;
  onChange: (caseType: CaseType) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

const linkButtonStyle = {
  color: '#718096',
  fontSize: '12px',
  fontWeight: 500,
  transition: 'color 0.2s ease-in-out',
  ':hover': { color: 'primary.main' },
};

const sectionLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#718096',
  textTransform: 'uppercase',
};

const addButtonStyle = {
  borderRadius: '24px',
  border: '1px dashed #93C5FD',
  color: '#1D4ED8',
  fontSize: '12px',
  lineHeight: '18px',
  padding: '6px 12px',
  ':hover': {
    backgroundColor: '#EFF6FF',
  },
};

interface PanelState {
  entryPoint: Extract<SsbEntryPoint, SsbEntryPoint.INCLUSION | SsbEntryPoint.EXCEPT>;
  editing?: SsbCondition;
}

function CaseTypeCard({ caseType, countyId, canDuplicate, onChange, onDuplicate, onRemove }: CaseTypeCardProps) {
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const cancelRenameRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const displayName = deriveCaseTypeName(caseType);
  const hasConditions = caseType.conditions.length > 0;
  const canAddCondition = caseType.conditions.length + caseType.excepts.length < MAX_CONDITIONS;
  const conditionLimitMessage = `Maximum number of conditions reached.`;
  const exceptDisabledReason = hasConditions ? conditionLimitMessage : 'Add at least one condition';

  const startRename = () => {
    setNameDraft(displayName);
    setIsEditingName(true);
  };

  const commitRename = () => {
    setIsEditingName(false);
    if (cancelRenameRef.current) {
      cancelRenameRef.current = false;
      return;
    }
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== displayName) {
      onChange({ ...caseType, customName: trimmed });
    }
  };

  const cancelRename = () => {
    cancelRenameRef.current = true;
    nameInputRef.current?.blur();
  };

  const handleCommit = (condition: SsbCondition) => {
    if (!panel) return;
    const list = panel.entryPoint === SsbEntryPoint.EXCEPT ? caseType.excepts : caseType.conditions;
    const exists = list.some((existing) => existing.id === condition.id);
    const next = exists
      ? list.map((existing) => (existing.id === condition.id ? condition : existing))
      : [...list, condition];

    onChange(
      panel.entryPoint === SsbEntryPoint.EXCEPT ? { ...caseType, excepts: next } : { ...caseType, conditions: next },
    );
  };

  const removeCondition = (entryPoint: PanelState['entryPoint'], conditionId: string) => {
    // close the editing panel on chip removal
    if (panel?.editing?.id === conditionId) setPanel(null);

    if (entryPoint === SsbEntryPoint.EXCEPT) {
      onChange({ ...caseType, excepts: caseType.excepts.filter((condition) => condition.id !== conditionId) });
    } else {
      onChange({ ...caseType, conditions: caseType.conditions.filter((condition) => condition.id !== conditionId) });
    }
  };

  const renderPanel = (entryPoint: PanelState['entryPoint']) =>
    panel?.entryPoint === entryPoint && (
      <ConditionPanel
        key={`${panel.entryPoint}-${panel.editing?.id ?? 'new'}`}
        entryPoint={panel.entryPoint}
        countyId={countyId}
        editingCondition={panel.editing}
        conflicts={[
          { conditions: caseType.conditions, message: 'This condition is already included in this case type.' },
          { conditions: caseType.excepts, message: 'This condition is already excluded from this case type.' },
        ]}
        onCommit={handleCommit}
        onClose={() => setPanel(null)}
      />
    );

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #CBD5E0',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        padding: '14px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minHeight: 242,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px' }}>
        <TextField
          variant='standard'
          color='accentBlue'
          fullWidth
          inputRef={nameInputRef}
          value={isEditingName ? nameDraft : displayName}
          onFocus={startRename}
          onChange={(event) => setNameDraft(event.target.value)}
          onBlur={commitRename}
          onKeyDown={(event) => {
            if (event.key === 'Escape') cancelRename();
            if (event.key === 'Enter') nameInputRef.current?.blur();
          }}
          slotProps={{
            input: {
              endAdornment: isEditingName && (
                <InputAdornment position='end' sx={{ gap: '10px' }}>
                  <Link
                    component='button'
                    type='button'
                    underline='none'
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={cancelRename}
                    sx={linkButtonStyle}
                  >
                    Cancel
                  </Link>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiInputBase-input': { fontSize: '14px', fontWeight: 600 },
            '& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
            '& .MuiInput-underline:after': { borderBottomWidth: '1px' },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <Tooltip title={canDuplicate ? '' : `Maximum number of case types reached.`}>
            <span>
              <Link
                component='button'
                type='button'
                underline='none'
                disabled={!canDuplicate}
                onClick={onDuplicate}
                sx={{ ...linkButtonStyle, ':disabled': { color: '#CBD5E0', cursor: 'not-allowed' } }}
              >
                Duplicate
              </Link>
            </span>
          </Tooltip>
          <Link component='button' type='button' underline='none' onClick={onRemove} sx={linkButtonStyle}>
            Remove
          </Link>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
        <Typography sx={sectionLabelStyle}>This case-type includes</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          {caseType.conditions.map((condition) => (
            <ConditionChip
              key={condition.id}
              condition={condition}
              variant='include'
              onEdit={() => setPanel({ entryPoint: SsbEntryPoint.INCLUSION, editing: condition })}
              onRemove={() => removeCondition(SsbEntryPoint.INCLUSION, condition.id)}
            />
          ))}
          <Tooltip title={canAddCondition ? '' : conditionLimitMessage}>
            <span>
              <Button
                disabled={!canAddCondition}
                sx={{ ...addButtonStyle, ':disabled': { color: '#93C5FD' } }}
                onClick={() => setPanel({ entryPoint: SsbEntryPoint.INCLUSION })}
              >
                + condition
              </Button>
            </span>
          </Tooltip>
        </Box>
        {renderPanel(SsbEntryPoint.INCLUSION)}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
        <Typography sx={{ ...sectionLabelStyle, color: '#B91C1C' }}>
          This case-type does{' '}
          <Typography component={'span'} sx={{ fontSize: 'inherit', fontWeight: 800 }}>
            NOT
          </Typography>{' '}
          include
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          {caseType.excepts.map((condition) => (
            <ConditionChip
              key={condition.id}
              condition={condition}
              variant='except'
              onEdit={() => setPanel({ entryPoint: SsbEntryPoint.EXCEPT, editing: condition })}
              onRemove={() => removeCondition(SsbEntryPoint.EXCEPT, condition.id)}
            />
          ))}
          <Tooltip title={hasConditions && canAddCondition ? '' : exceptDisabledReason}>
            <span>
              <Button
                disabled={!hasConditions || !canAddCondition}
                sx={{
                  ...addButtonStyle,
                  borderColor: '#FECACA',
                  color: hasConditions && canAddCondition ? '#B91C1C' : '#FEB2B2 !important',
                  ':hover': { backgroundColor: '#FEF2F2' },
                }}
                onClick={() => setPanel({ entryPoint: SsbEntryPoint.EXCEPT })}
              >
                + except
              </Button>
            </span>
          </Tooltip>
        </Box>
        {renderPanel(SsbEntryPoint.EXCEPT)}
      </Box>
    </Box>
  );
}

export default CaseTypeCard;
